import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function LiveUpdates() {
  const { canWrite } = useAuth();
  const [items, setItems] = useState([]);

  const load = async () => {
    const { data } = await api.get("/knowledge", { params: { status: "outdated" } });
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    await api.post(`/knowledge/${id}/approve`);
    toast.success("Marked current");
    load();
  };
  const remove = async (id) => {
    if (!confirm("Delete this entry permanently?")) return;
    await api.delete(`/knowledge/${id}`);
    toast("Deleted");
    load();
  };

  return (
    <div className="p-8 max-w-4xl">
      <header className="mb-8">
        <p className="eyebrow mb-3">Live Updates</p>
        <h1 className="font-display text-3xl">Review queue</h1>
        <p className="mt-2 text-muted-foreground">Approve, update, or remove entries flagged as outdated.</p>
      </header>

      {items.length === 0 ? (
        <div className="card-flat p-10 text-center text-muted-foreground text-sm">
          Nothing flagged. Your knowledge base is up to date.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((k) => (
            <div key={k.id} className="card-flat p-5" data-testid={`update-row-${k.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="secondary" className="text-[10px]">{k.category}</Badge>
                    <Badge variant="outline" className="text-[10px]">{k.department}</Badge>
                    <span className="text-[10px] uppercase text-amber-500 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Outdated
                    </span>
                  </div>
                  <h3 className="font-display text-lg mb-1">{k.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{k.summary}</p>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  {canWrite ? (
                    <>
                      <Button size="sm" onClick={() => approve(k.id)} className="rounded-full" data-testid={`update-approve-${k.id}`}>
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(k.id)} className="rounded-full text-destructive" data-testid={`update-delete-${k.id}`}>
                        <Trash2 className="w-4 h-4 mr-1" /> Delete
                      </Button>
                    </>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground" data-testid={`update-viewer-${k.id}`}>Read-only</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
